import { MapPin, Phone, Mail, Clock, Wallet, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { COMPANY, OFFICE_HOURS } from "../lib/constants";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact 1st Home Care | Home Care in Springfield, Fairfax & Northern Virginia"
        description="Contact 1st Home Care Services in Springfield, VA. Call 703-981-8201 or send a request online. Serving Springfield, Fairfax, Arlington, Alexandria & Falls Church."
        canonical="https://1sthomecare.com/contact"
      />

      <section className="container-pad section-pad">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            Contact Us
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-blue-dark text-balance">
            Speak with a nurse today.
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Whether you're starting care for a loved one or just exploring your
            options, our team is ready to help. Call us, email, or send a quick
            request — and a care coordinator will respond promptly.
          </p>
        </div>
      </section>

      <section className="container-pad pb-16 md:pb-24">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            <div
              data-testid="contact-card-address"
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex gap-4"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading text-base font-semibold text-brand-blue-dark">Office</div>
                <div className="text-sm text-gray-600 mt-1">{COMPANY.fullAddress}</div>
              </div>
            </div>

            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="contact-card-phone"
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex gap-4 hover:border-brand-blue transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <div className="font-heading text-base font-semibold text-brand-blue-dark">Phone</div>
                <div className="text-sm text-brand-blue font-semibold mt-1">{COMPANY.phone}</div>
                <div className="text-xs text-gray-500 mt-0.5">Tap to call from any device</div>
              </div>
            </a>

            <div
              data-testid="contact-card-email"
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm flex gap-4"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading text-base font-semibold text-brand-blue-dark">Email</div>
                {COMPANY.emails.map((em) => (
                  <a
                    key={em}
                    href={`mailto:${em}`}
                    className="block text-sm text-brand-blue mt-1 hover:text-brand-amber-dark whitespace-nowrap overflow-hidden text-ellipsis"
                    title={em}
                  >
                    {em}
                  </a>
                ))}
              </div>
            </div>

            <div
              data-testid="contact-card-hours"
              className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm"
            >
              <div className="flex gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-heading text-base font-semibold text-brand-blue-dark">Office Hours</div>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {OFFICE_HOURS.map((h) => (
                      <li
                        key={h.day}
                        data-testid={`contact-hours-${h.day.toLowerCase()}`}
                        className="flex items-center justify-between gap-3 border-b border-gray-100 last:border-0 pb-1.5 last:pb-0"
                      >
                        <span className="font-semibold uppercase tracking-wider text-gray-700 w-12">{h.day}</span>
                        <span className="text-gray-600">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 text-xs text-brand-blue-dark font-medium">
                    24/7 care available • Same-day intake
                  </div>
                </div>
              </div>
            </div>

            <Link
              to="/contact/payment"
              data-testid="contact-card-payment"
              className="block bg-brand-blue-dark text-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 group"
            >
              <div className="flex gap-4 items-start">
                <div className="w-11 h-11 rounded-xl bg-brand-amber text-white flex items-center justify-center shrink-0">
                  <Wallet className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-heading text-base font-semibold">Payment Options</div>
                  <div className="text-sm text-white/80 mt-1">
                    Medicaid accepted &amp; private pay available — see how billing works.
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-amber-light group-hover:gap-2 transition-all">
                    View payment page <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <div className="lg:col-span-3">
            <LeadForm title="Request Care" />
          </div>
        </div>
      </section>

      {/* MAP */}
      <section data-testid="map-section" className="container-pad pb-20">
        <div className="rounded-3xl overflow-hidden shadow-xl border border-gray-100">
          <iframe
            title="1st Home Care Services - Springfield, VA"
            data-testid="google-map-iframe"
            src="https://www.google.com/maps?q=6315%20Backlick%20Road%20Suite%20101%20Springfield%20VA%2022150&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </>
  );
}
